import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  X, Search, Plus, Minus, Trash2, User, Loader2,
  Stethoscope, FileText,
  CheckCircle, Clock, AlertCircle,
  ChevronDown, Receipt,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { searchPatients, getPatientDeposits } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { usePermission } from '@/hooks/usePermission';
import { displayAge } from '@/lib/patientAge';

// ─── Types ────────────────────────────────────────────────────────────────────

interface BillingService {
  id: string;
  name: string;
  price: number;
  tax_rate?: number;
  category: string;
  is_frequent?: boolean;
  is_active?: boolean;
}

interface Patient {
  id: string;
  name: string;
  phone?: string;
  address?: string;
  age?: number | string;
  date_of_birth?: string;
  gender?: string;
  mrn?: string;
}

interface Doctor {
  id: string;
  name: string;
}

interface BillItem {
  id: string;
  service_id?: string;
  service_name: string;
  category?: string;
  item_type?: 'consultation' | 'procedure' | 'investigation' | 'other';
  quantity: number;
  unit_price: number;
  discount: number;
  tax_rate: number;
  tax_amount: number;
  total_price: number;
}

interface MedicalBillingModalProps {
  isOpen: boolean;
  onClose: () => void;
  patient?: Patient | null;
  services: BillingService[];
  onCreateInvoice: (invoiceData: {
    items: Array<Omit<BillItem, 'id' | 'discount'> & { discount_amount: number }>;
    discount: number;
    discountType: 'flat' | 'percent';
    taxAmount: number;
    paidAmount: number;
    payments: Array<{
      amount: number;
      payment_mode: 'cash' | 'upi' | 'card' | 'cheque' | 'advance';
      reference_number?: string;
      upi_app?: string;
    }>;
    finalTotal: number;
    doctorId?: string;
    patient?: Patient;
    invoiceDate?: string;
    notes?: string;
    balance_forward?: number;
    balance_forward_settle?: number;
    balance_forward_payment_mode?: string;
    depositAmount?: number;
    depositPaymentMode?: string;
  }) => void;
  loading?: boolean;
  clinicId: string;
  doctorId?: string;
  doctors?: Doctor[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function debounce<T extends (...args: any[]) => any>(func: T, wait: number) {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

const PAYMENT_METHODS = ['Cash', 'UPI', 'Card', 'Cheque'];

// Left rail color per item type — makes the bill instantly scannable
const ITEM_RAIL: Record<string, string> = {
  consultation: 'bg-primary/80',
  investigation: 'bg-blue-500/70',
  procedure: 'bg-destructive/70',
  other: 'bg-muted-foreground/30',
};

const ITEM_TYPE_MAP: Record<string, 'consultation' | 'procedure' | 'investigation' | 'other'> = {
  consultation: 'consultation',
  procedure: 'procedure',
  investigation: 'investigation',
  diagnostic: 'investigation',
};

function lineTax(quantity: number, unitPrice: number, taxRate: number) {
  return Math.round(quantity * unitPrice * (taxRate / 100) * 100) / 100;
}

interface PaymentRowState {
  id: string;
  mode: string;
  amount: number;
  referenceNumber: string;
  upiApp: string;
}

function makePaymentRow(mode: string = 'Cash', amount = 0): PaymentRowState {
  return {
    id: crypto.randomUUID?.() ?? Math.random().toString(36).slice(2),
    mode, amount, referenceNumber: '', upiApp: '',
  };
}

function localToday(): string {
  const d = new Date();
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
  ].join('-');
}

function deriveStatus(paid: number, total: number): 'Paid' | 'Partial' | 'Pending' {
  if (total <= 0) return 'Pending';
  if (paid >= total) return 'Paid';
  if (paid > 0) return 'Partial';
  return 'Pending';
}

// ─── Summary row helper ────────────────────────────────────────────────────────

function SummaryRow({ label, value, bold = false, valueClass = '' }: {
  label: string; value: string; bold?: boolean; valueClass?: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className={`text-xs ${bold ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>{label}</span>
      <span className={`text-xs font-semibold tabular-nums ${bold ? 'text-foreground' : ''} ${valueClass}`}>{value}</span>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function MedicalBillingModal({
  isOpen, onClose,
  patient = null, services = [],
  onCreateInvoice, loading = false,
  clinicId, doctorId, doctors = [],
}: MedicalBillingModalProps) {
  const { currentUser } = useAuth();
  const isFrontDesk = !currentUser?.doctor_id;
  const { can } = usePermission();
  const canDiscount = can('billing:discount');

  // ── Bill state ───────────────────────────────────────────────────────────
  const [billItems, setBillItems]       = useState<BillItem[]>([]);
  const [discount, setDiscount]         = useState(0);
  const [discountType, setDiscountType] = useState<'flat' | 'percent'>('flat');
  const [payments, setPayments]         = useState<PaymentRowState[]>(() => [makePaymentRow()]);
  const [error, setError]               = useState<string | null>(null);
  const [billDate, setBillDate]         = useState(() => localToday());
  const [notes, setNotes]               = useState('');

  // ── Patient search ───────────────────────────────────────────────────────
  const [selectedPatient, setSelectedPatient]   = useState<Patient | null>(patient ?? null);
  const [patientQuery, setPatientQuery]         = useState('');
  const [patientResults, setPatientResults]     = useState<Patient[]>([]);
  const [patientSearching, setPatientSearching] = useState(false);
  const [availableAdvance, setAvailableAdvance] = useState(0);
  const [outstanding, setOutstanding]           = useState(0);
  const [pendingAmount, setPendingAmount]       = useState(0);
  const patientInputRef = useRef<HTMLInputElement>(null);
  const prevFinalTotalRef = useRef(0);

  const todayStr = useMemo(() => localToday(), []);
  const isBackdated = useMemo(() => {
    if (!billDate) return false;
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 30);
    return new Date(billDate) < cutoff;
  }, [billDate]);

  // ── Service search (inline at bottom of table) ───────────────────────────
  const [serviceSearch, setServiceSearch]         = useState('');
  const [serviceDropdownOpen, setServiceDropdownOpen] = useState(false);
  const serviceSearchRef  = useRef<HTMLInputElement>(null);
  const serviceDropdownRef = useRef<HTMLDivElement>(null);

  // ── Balance forward (Add to Bill) ───────────────────────────────────────
  const [balanceForward, setBalanceForward] = useState(0);

  // ── Deposit collection ───────────────────────────────────────────────────
  const [depositAmount, setDepositAmount] = useState(0);
  const [depositOpen, setDepositOpen]     = useState(false);

  // ── Doctor / misc ────────────────────────────────────────────────────────
  const [selectedDoctorId, setSelectedDoctorId] = useState(doctorId ?? '');
  const [unitPriceInputs, setUnitPriceInputs]   = useState<Record<string, string>>({});

  // ── Sync props ───────────────────────────────────────────────────────────
  useEffect(() => { setSelectedPatient(patient ?? null); }, [patient]);
  useEffect(() => { setSelectedDoctorId(doctorId ?? ''); }, [doctorId]);

  // ── Reset on open ────────────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setBillItems([]); setDiscount(0); setDiscountType('flat');
      setPayments([makePaymentRow()]); setError(null);
      setPatientQuery(''); setPatientResults([]);
      setBillDate(localToday());
      setServiceSearch(''); setServiceDropdownOpen(false);
      setNotes(''); setUnitPriceInputs({});
      setAvailableAdvance(0); setOutstanding(0); setPendingAmount(0); setBalanceForward(0);
      setDepositAmount(0); setDepositOpen(false);
      prevFinalTotalRef.current = 0;
      setSelectedPatient(patient ?? null);
      setSelectedDoctorId(doctorId ?? '');
    }
  }, [isOpen]);

  // ── Close service dropdown on outside click ──────────────────────────────
  useEffect(() => {
    if (!serviceDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        serviceDropdownRef.current && !serviceDropdownRef.current.contains(e.target as Node) &&
        serviceSearchRef.current && !serviceSearchRef.current.contains(e.target as Node)
      ) {
        setServiceDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [serviceDropdownOpen]);

  // ── Calculations ─────────────────────────────────────────────────────────
  const subtotal = useMemo(
    () => billItems.reduce((s, i) => s + (parseFloat(String(i.total_price)) || 0), 0),
    [billItems]
  );

  const discountAmount = useMemo(() => {
    if (discountType === 'percent') return Math.min(subtotal, (subtotal * discount) / 100);
    return Math.min(subtotal, Math.max(0, discount));
  }, [discount, discountType, subtotal]);

  const taxTotal = useMemo(
    () => billItems.reduce((s, i) => s + (i.tax_amount || 0), 0),
    [billItems]
  );

  const finalTotal = useMemo(
    () => Math.max(0, subtotal - discountAmount + taxTotal),
    [subtotal, discountAmount, taxTotal]
  );

  const paidAmount = useMemo(
    () => payments.reduce((s, p) => s + (p.amount || 0), 0),
    [payments]
  );

  // totalToCollect = invoice total + any explicitly added balance forward + deposit to collect
  const totalToCollect = finalTotal + balanceForward + depositAmount;
  // balanceAmount = total still to collect (bill + settlement + deposit combined)
  const balanceAmount  = Math.max(0, totalToCollect - paidAmount);
  // Excess collected toward the balance forward settlement
  const settlementAmount = useMemo(
    () => balanceForward > 0 ? Math.min(Math.max(0, paidAmount - finalTotal), balanceForward) : 0,
    [paidAmount, finalTotal, balanceForward]
  );
  // True overpayment = collected more than the total to collect (error)
  const trueOverpaid = useMemo(
    () => Math.max(0, paidAmount - totalToCollect),
    [paidAmount, totalToCollect]
  );
  const hasGST = useMemo(() => billItems.some(i => i.tax_rate > 0), [billItems]);
  // For status: only count the bill portion (cap at finalTotal)
  const billPaidAmount = Math.min(paidAmount, finalTotal);
  const paymentStatus  = deriveStatus(billPaidAmount, finalTotal);

  // Auto-fill payment amount when bill total or balance forward changes
  useEffect(() => {
    if (payments.length === 1 && payments[0].amount === prevFinalTotalRef.current && payments[0].amount !== totalToCollect) {
      setPayments([{ ...payments[0], amount: totalToCollect }]);
    }
    prevFinalTotalRef.current = totalToCollect;
  }, [totalToCollect]);

  const paymentMethods = useMemo(
    () => availableAdvance > 0 ? [...PAYMENT_METHODS, 'Advance'] : PAYMENT_METHODS,
    [availableAdvance]
  );

  // ── Patient account snapshot ──────────────────────────────────────────────
  useEffect(() => {
    if (!selectedPatient?.id || !clinicId) { setAvailableAdvance(0); setOutstanding(0); return; }
    let cancelled = false;
    getPatientDeposits(selectedPatient.id, clinicId)
      .then(res => {
        if (cancelled) return;
        setAvailableAdvance(Number(res?.available_advance || 0));
        setOutstanding(Number(res?.outstanding || 0));
        setPendingAmount(Number(res?.pending_amount || 0));
      })
      .catch(() => { if (!cancelled) { setAvailableAdvance(0); setOutstanding(0); } });
    return () => { cancelled = true; };
  }, [selectedPatient?.id, clinicId]);

  // ── Patient search ────────────────────────────────────────────────────────
  const debouncedSearch = useCallback(
    debounce(async (q: string) => {
      if (q.trim().length < 2) { setPatientResults([]); setPatientSearching(false); return; }
      setPatientSearching(true);
      try {
        const res = await searchPatients(q, clinicId);
        setPatientResults(Array.isArray(res) ? res.slice(0, 8) : []);
      } catch { setPatientResults([]); }
      finally { setPatientSearching(false); }
    }, 300),
    [clinicId]
  );

  useEffect(() => {
    if (patientQuery.trim().length >= 2) { setPatientSearching(true); debouncedSearch(patientQuery); }
    else setPatientResults([]);
  }, [patientQuery, debouncedSearch]);

  // ── Bill item operations ──────────────────────────────────────────────────

  // base = qty * price - line discount (pre-tax subtotal)
  function lineBase(qty: number, unitPrice: number, discount: number) {
    return Math.max(0, qty * unitPrice - discount);
  }

  const addService = (svc: BillingService) => {
    if (!svc.is_active) { setError('This service is inactive'); return; }
    setError(null);
    const price   = parseFloat(String(svc.price))    || 0;
    const taxRate = parseFloat(String(svc.tax_rate)) || 0;
    const existing = billItems.find(i => i.service_id === svc.id);
    if (existing) {
      updateQty(existing.id, existing.quantity + 1);
    } else {
      const id = crypto.randomUUID?.() ?? Math.random().toString(36).slice(2);
      setBillItems(prev => [...prev, {
        id, service_id: svc.id, service_name: svc.name,
        category: svc.category, item_type: ITEM_TYPE_MAP[svc.category] ?? 'other',
        quantity: 1, unit_price: price, discount: 0,
        tax_rate: taxRate, tax_amount: lineTax(price, 1, taxRate), total_price: price,
      }]);
    }
  };

  const updateQty = (id: string, qty: number) => {
    if (qty <= 0) { removeItem(id); return; }
    setBillItems(prev => prev.map(i => {
      if (i.id !== id) return i;
      const base = lineBase(qty, i.unit_price, i.discount);
      return { ...i, quantity: qty, total_price: base, tax_amount: lineTax(base, 1, i.tax_rate) };
    }));
  };

  const updatePrice = (id: string, price: number) => {
    const p = Math.max(0, parseFloat(String(price)) || 0);
    setBillItems(prev => prev.map(i => {
      if (i.id !== id) return i;
      const base = lineBase(i.quantity, p, i.discount);
      return { ...i, unit_price: p, total_price: base, tax_amount: lineTax(base, 1, i.tax_rate) };
    }));
  };

  const updateDiscount = (id: string, discount: number) => {
    const d = Math.max(0, discount);
    setBillItems(prev => prev.map(i => {
      if (i.id !== id) return i;
      const base = lineBase(i.quantity, i.unit_price, d);
      return { ...i, discount: d, total_price: base, tax_amount: lineTax(base, 1, i.tax_rate) };
    }));
  };

  const updateTaxRate = (id: string, rate: number) => {
    const r = Math.min(100, Math.max(0, parseFloat(String(rate)) || 0));
    setBillItems(prev => prev.map(i => {
      if (i.id !== id) return i;
      return { ...i, tax_rate: r, tax_amount: lineTax(i.total_price, 1, r) };
    }));
  };

  const removeItem = (id: string) => {
    setBillItems(prev => prev.filter(i => i.id !== id));
    setUnitPriceInputs(prev => { const n = { ...prev }; delete n[id]; return n; });
  };

  // ── Payment operations ────────────────────────────────────────────────────
  const addPaymentRow = () => {
    setPayments(prev => {
      const used = new Set(prev.map(p => p.mode));
      const nextMode = paymentMethods.find(m => !used.has(m)) ?? 'Cash';
      const remaining = Math.max(0, totalToCollect - prev.reduce((s, p) => s + (p.amount || 0), 0));
      const amount = nextMode === 'Advance' ? Math.min(remaining, availableAdvance) : remaining;
      return [...prev, makePaymentRow(nextMode, amount)];
    });
  };

  const removePaymentRow = (id: string) => {
    setPayments(prev => prev.length > 1 ? prev.filter(p => p.id !== id) : prev);
  };

  const updatePaymentRow = (id: string, patch: Partial<PaymentRowState>) => {
    setPayments(prev => prev.map(p => p.id === id ? { ...p, ...patch } : p));
  };

  const setRowToFull = (id: string) => {
    setPayments(prev => {
      const row = prev.find(p => p.id === id);
      const others = prev.filter(p => p.id !== id).reduce((s, p) => s + (p.amount || 0), 0);
      const remaining = Math.max(0, totalToCollect - others);
      const cap = row?.mode === 'Advance' ? Math.min(remaining, availableAdvance) : remaining;
      return prev.map(p => p.id === id ? { ...p, amount: cap } : p);
    });
  };

  // ── Filtered services ─────────────────────────────────────────────────────
  const filteredServices = useMemo(() => {
    const list = services.filter(s => s.is_active !== false);
    if (!serviceSearch) return list;
    const q = serviceSearch.toLowerCase();
    return list.filter(s => s.name.toLowerCase().includes(q) || s.category?.toLowerCase().includes(q));
  }, [services, serviceSearch]);



  // ── Submit ────────────────────────────────────────────────────────────────
  const handleCreate = () => {
    if (!selectedPatient?.id) { setError('Please select a patient'); return; }
    if (billDate > localToday()) { setError('Bill date cannot be in the future'); return; }  // Bug 9: fresh date each time
    if (isFrontDesk && !selectedDoctorId) { setError('Please select a doctor'); return; }
    if (billItems.length === 0) { setError('Add at least one service'); return; }
    if (billItems.some(i => !i.service_name || i.unit_price < 0 || i.quantity <= 0)) {
      setError('Some items have invalid data'); return;
    }

    // Bug 7: include deposit in overpayment error text
    if (trueOverpaid > 0) {
      const parts = ['bill total'];
      if (balanceForward > 0) parts.push('balance forward');
      if (depositAmount > 0)  parts.push('deposit');
      setError(`Received amount exceeds ${parts.join(' + ')}`); return;
    }

    // Bug 3: deposit cannot be collected if all payment is via advance (circular)
    if (depositAmount > 0 && !payments.some(p => p.mode !== 'Advance' && p.amount > 0)) {
      setError('Cannot collect deposit using advance balance — add a cash/UPI/card payment'); return;
    }

    // Bug 1: deposit must be covered by the payment amount after bill + settlement
    if (depositAmount > 0) {
      const depositCovered = Math.max(0, paidAmount - billPaidAmount - settlementAmount);
      if (depositCovered < depositAmount) {
        setError('Deposit not covered — payment must cover the bill total before a deposit can be collected'); return;
      }
    }

    const advanceTotal = payments.filter(p => p.mode === 'Advance').reduce((s, p) => s + (p.amount || 0), 0);
    // Bug 2: settlement only draws from advance if there are NO non-advance payment rows
    const hasNonAdvancePayment = payments.some(p => p.mode !== 'Advance' && p.amount > 0);
    const advanceSettlement = (!hasNonAdvancePayment && settlementAmount > 0) ? settlementAmount : 0;
    if (advanceTotal > availableAdvance) { setError('Advance amount exceeds available balance'); return; }
    if (advanceTotal + advanceSettlement > availableAdvance) { setError('Total advance usage (bill + dues) exceeds available balance'); return; }
    setError(null);

    // Cap payment rows at finalTotal FIFO — any excess is the settlement portion
    const billPayments: typeof payments = [];
    let remaining = finalTotal;
    for (const p of payments.filter(r => r.amount > 0)) {
      if (remaining <= 0) break;
      const take = Math.min(p.amount, remaining);
      billPayments.push({ ...p, amount: take });
      remaining -= take;
    }

    // Primary non-advance mode used for the settlement entry
    const primaryMode = billPayments.find(p => p.mode !== 'Advance')?.mode ?? payments[0]?.mode ?? 'Cash';

    onCreateInvoice({
      items: billItems.map(({ id: _id, discount, ...rest }) => ({ ...rest, discount_amount: discount })),
      discount, discountType, taxAmount: taxTotal,
      paidAmount: billPaidAmount,
      payments: billPayments.map(p => ({
        amount: p.amount,
        payment_mode: (p.mode === 'Advance' ? 'advance' : p.mode.toLowerCase()) as any,
        reference_number: p.referenceNumber.trim() || undefined,
        upi_app: p.mode === 'UPI' ? (p.upiApp || undefined) : undefined,
      })),
      finalTotal, invoiceDate: billDate,
      doctorId: selectedDoctorId || undefined,
      patient: selectedPatient,
      notes: notes.trim() || undefined,
      balance_forward:        balanceForward > 0 ? balanceForward : undefined,
      balance_forward_settle: settlementAmount > 0 ? settlementAmount : undefined,
      balance_forward_payment_mode: settlementAmount > 0 ? primaryMode.toLowerCase() : undefined,
      depositAmount:        depositAmount > 0 ? depositAmount : undefined,
      depositPaymentMode:   depositAmount > 0 ? primaryMode.toLowerCase() : undefined,
    });
  };

  const hasPatient = !!selectedPatient;
  const showDoctorDropdown = isFrontDesk;

  const statusConfig = {
    Paid:    { color: 'hsl(var(--success))',          bg: 'hsl(var(--success) / 0.12)',          icon: CheckCircle },
    Partial: { color: 'hsl(var(--warning))',          bg: 'hsl(var(--warning) / 0.12)',          icon: AlertCircle },
    Pending: { color: 'hsl(var(--muted-foreground))', bg: 'hsl(var(--muted))',                   icon: Clock },
  }[paymentStatus];

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        showClose={false}
        className="max-w-[1080px] w-full h-[90vh] p-0 gap-0 flex flex-col overflow-hidden rounded-2xl shadow-2xl border-0"
      >
        <DialogTitle className="sr-only">
          {hasPatient ? `New Bill — ${selectedPatient!.name}` : 'New Bill'}
        </DialogTitle>

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div
          className="flex items-center gap-3 px-5 py-3 shrink-0"
          style={{
            background: 'hsl(var(--primary))',
            backgroundImage: 'radial-gradient(circle at 1px 1px, hsl(var(--primary-foreground)/0.06) 1px, transparent 0)',
            backgroundSize: '20px 20px',
          }}
        >
          {/* Icon */}
          <div className="w-8 h-8 rounded-xl bg-white/15 border border-white/10 flex items-center justify-center shrink-0">
            <Receipt className="w-4 h-4 text-white" />
          </div>

          {/* Patient info */}
          <div className="flex-1 min-w-0">
            {hasPatient ? (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-white font-bold text-sm capitalize tracking-tight">{selectedPatient!.name}</span>

                {(() => {
                  const ageStr = displayAge(
                    typeof selectedPatient!.age === 'number' ? selectedPatient!.age : undefined,
                    selectedPatient!.date_of_birth
                  );
                  return (ageStr || selectedPatient!.gender) ? (
                    <span className="text-white/55 text-xs">
                      {ageStr}{ageStr && selectedPatient!.gender ? ' · ' : ''}{selectedPatient!.gender ?? ''}
                    </span>
                  ) : null;
                })()}

                {selectedPatient!.mrn && (
                  <span className="text-white/35 font-mono text-[11px]">#{selectedPatient!.mrn}</span>
                )}

                {availableAdvance > 0 && (
                  <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-white/90"
                    style={{ color: 'hsl(var(--success))' }}>
                    ₹{availableAdvance.toLocaleString('en-IN')} advance
                  </span>
                )}
                {outstanding > 0 && balanceForward === 0 && (
                  <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-white/90"
                    style={{ color: 'hsl(var(--destructive))' }}>
                    ₹{outstanding.toLocaleString('en-IN')} due
                  </span>
                )}

                {!patient && (
                  <button
                    onClick={() => { setSelectedPatient(null); setBillItems([]); setPayments([makePaymentRow()]); setDiscount(0); setBalanceForward(0); }}
                    className="text-white/30 hover:text-white/70 transition-colors ml-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            ) : (
              <span className="text-white/45 text-sm">Select a patient to begin</span>
            )}
          </div>

          {/* Doctor + Date + Close */}
          <div className="flex items-center gap-2 shrink-0">
            {showDoctorDropdown && (
              <div className="relative">
                <Stethoscope className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-white/40 pointer-events-none" />
                <select
                  value={selectedDoctorId}
                  onChange={e => setSelectedDoctorId(e.target.value)}
                  className="text-xs bg-white/10 border border-white/15 text-white rounded-lg pl-7 pr-6 py-1.5 appearance-none focus:outline-none focus:bg-white/15 focus:border-white/30 [&>option]:text-foreground max-w-[140px] transition-colors"
                >
                  <option value="">{doctors.length ? 'Doctor' : 'No doctors'}</option>
                  {doctors.map(d => (
                    <option key={d.id} value={d.id}>{d.name.startsWith('Dr.') ? d.name : `Dr. ${d.name}`}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-white/35 pointer-events-none" />
              </div>
            )}

            {/* Date pill */}
            <div className="relative">
              <input
                type="date"
                value={billDate}
                max={todayStr}
                onChange={e => setBillDate(e.target.value)}
                className="opacity-0 absolute inset-0 w-full cursor-pointer"
              />
              <div className="text-xs text-white/75 bg-white/10 border border-white/15 rounded-lg px-3 py-1.5 pointer-events-none min-w-[88px] text-center tabular-nums">
                {billDate.split('-').reverse().join('/')}
              </div>
            </div>

            <button
              onClick={() => setBillDate(localToday())}
              className="text-xs text-white/45 hover:text-white/75 transition-colors font-medium"
            >
              Today
            </button>

            <div className="w-px h-4 bg-white/15" />

            <button
              onClick={onClose}
              className="w-7 h-7 flex items-center justify-center rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-all"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* ── Alerts ───────────────────────────────────────────────────────── */}
        {isBackdated && hasPatient && (
          <div className="mx-4 mt-2.5 flex items-center gap-2 px-3 py-2 bg-warning/8 border border-warning/25 rounded-xl text-warning shrink-0">
            <AlertCircle className="w-3.5 h-3.5 shrink-0" />
            <span className="text-xs font-medium">
              Bill dated {new Date(billDate + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} — more than 30 days ago. Verify before saving.
            </span>
          </div>
        )}

        {error && (
          <div className="mx-4 mt-2.5 flex items-center gap-2 px-3 py-2 bg-destructive/6 border border-destructive/20 rounded-xl shrink-0">
            <AlertCircle className="w-3.5 h-3.5 shrink-0 text-destructive" />
            <span className="flex-1 text-xs text-destructive">{error}</span>
            <button onClick={() => setError(null)} className="text-destructive/40 hover:text-destructive transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* ── Body ─────────────────────────────────────────────────────────── */}
        {!hasPatient ? (

          /* ── Patient search ── */
          <div className="flex-1 flex flex-col items-center justify-center px-8 pb-8 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-primary/8 border border-primary/15 flex items-center justify-center mx-auto mb-4">
                <User className="w-7 h-7 text-primary/60" />
              </div>
              <h2 className="text-lg font-bold text-foreground tracking-tight">Find a patient</h2>
              <p className="text-sm text-muted-foreground mt-1">Search by name or phone number to start a new bill</p>
            </div>

            <div className="w-full max-w-md relative">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/60" />
                <input
                  ref={patientInputRef}
                  value={patientQuery}
                  onChange={e => setPatientQuery(e.target.value)}
                  placeholder="Patient name or phone…"
                  className="w-full pl-11 pr-10 py-3 text-sm border-2 border-border rounded-xl focus:outline-none focus:border-primary transition-colors bg-background"
                  autoFocus
                />
                {patientSearching && (
                  <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
                )}
              </div>

              {patientResults.length > 0 && (
                <div className="absolute left-0 right-0 top-full mt-2 z-20 bg-popover border border-border rounded-xl shadow-xl overflow-hidden">
                  {patientResults.map((p, i) => (
                    <button
                      key={p.id}
                      onClick={() => { setSelectedPatient(p); setPatientQuery(''); setPatientResults([]); }}
                      className={cn('w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-muted/50 transition-colors',
                        i > 0 && 'border-t border-border/50'
                      )}
                    >
                      <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0 text-sm font-bold">
                        {p.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-foreground capitalize truncate">{p.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {[displayAge(typeof p.age === 'number' ? p.age : undefined, p.date_of_birth), p.gender, p.phone, p.mrn ? `#${p.mrn}` : ''].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

        ) : (

          /* ── POS split layout ── */
          <div className="flex flex-1 overflow-hidden">

            {/* ══ LEFT PANE: Bill items ══ */}
            <div className="flex-1 flex flex-col overflow-hidden border-r border-border">
              <div className="flex-1 overflow-y-auto">
                <table className="w-full text-sm border-collapse">
                  <thead className="sticky top-0 z-10">
                    <tr className="border-b border-border bg-muted/20 text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
                      <th className="text-left px-3 py-2 w-10">#</th>
                      <th className="text-left px-3 py-2">Service Name</th>
                      <th className="text-center px-2 py-2 w-24">Qty</th>
                      <th className="text-right px-3 py-2 w-28">Unit Price</th>
                      {hasGST && <th className="text-right px-2 py-2 w-14">GST%</th>}
                      <th className="text-right px-3 py-2 w-24">Discount</th>
                      <th className="text-right px-3 py-2 w-24">Total</th>
                      <th className="w-7 px-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {billItems.map((item, idx) => {
                      const railClass = ITEM_RAIL[item.item_type ?? 'other'] ?? ITEM_RAIL.other;
                      return (
                        <tr key={item.id} className="border-b border-border/40 hover:bg-muted/20 group transition-colors">
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1.5">
                              <div className={`w-1.5 h-5 rounded-full flex-shrink-0 ${railClass}`} />
                              <span className="text-xs text-muted-foreground/40 font-mono">{idx + 1}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <div className="font-semibold text-foreground text-sm">{item.service_name}</div>
                            {item.category && <div className="text-[10px] text-muted-foreground capitalize">{item.category}</div>}
                          </td>
                          <td className="px-2 py-2">
                            <div className="flex items-center justify-center gap-0.5">
                              <button onClick={() => updateQty(item.id, item.quantity - 1)}
                                className="w-5 h-5 flex items-center justify-center rounded bg-muted hover:bg-muted/80 text-muted-foreground transition-colors">
                                <Minus className="w-2 h-2" />
                              </button>
                              <input type="number" min={1} value={item.quantity}
                                onKeyDown={e => ['e','E','-','+'].includes(e.key) && e.preventDefault()}
                                onChange={e => updateQty(item.id, parseInt(e.target.value) || 1)}
                                className="w-8 text-center text-sm font-bold border border-border rounded focus:outline-none focus:border-primary py-0.5 tabular-nums" />
                              <button onClick={() => updateQty(item.id, item.quantity + 1)}
                                className="w-5 h-5 flex items-center justify-center rounded bg-muted hover:bg-muted/80 text-muted-foreground transition-colors">
                                <Plus className="w-2 h-2" />
                              </button>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <div className="relative inline-flex items-center">
                              <span className="absolute left-1.5 text-muted-foreground text-xs pointer-events-none">₹</span>
                              <input type="number" min={0}
                                value={unitPriceInputs[item.id] ?? String(item.unit_price)}
                                onKeyDown={e => ['e','E','-','+'].includes(e.key) && e.preventDefault()}
                                onChange={e => {
                                  const raw = e.target.value;
                                  setUnitPriceInputs(prev => ({ ...prev, [item.id]: raw }));
                                  const parsed = parseFloat(raw);
                                  if (!isNaN(parsed) && parsed >= 0) updatePrice(item.id, parsed);
                                }}
                                onBlur={e => {
                                  const parsed = parseFloat(e.target.value);
                                  updatePrice(item.id, isNaN(parsed) || parsed < 0 ? 0 : parsed);
                                  setUnitPriceInputs(prev => { const n = { ...prev }; delete n[item.id]; return n; });
                                }}
                                placeholder="0"
                                className="w-20 text-right text-sm font-semibold border border-border rounded focus:outline-none focus:border-primary pl-4 pr-1.5 py-1 tabular-nums" />
                            </div>
                          </td>
                          {hasGST && (
                            <td className="px-2 py-2 text-right">
                              <input type="number" min={0} max={100}
                                value={item.tax_rate === 0 ? '' : item.tax_rate}
                                onKeyDown={e => ['e','E','-','+'].includes(e.key) && e.preventDefault()}
                                onChange={e => updateTaxRate(item.id, parseFloat(e.target.value) || 0)}
                                placeholder="0"
                                className="w-10 text-right text-sm border border-border rounded focus:outline-none focus:border-primary px-1 py-1" />
                            </td>
                          )}
                          <td className="px-3 py-2 text-right">
                            <div className="relative inline-flex items-center">
                              <span className="absolute left-1.5 text-muted-foreground text-xs pointer-events-none">₹</span>
                              <input type="number" min={0}
                                value={item.discount === 0 ? '' : item.discount}
                                onKeyDown={e => ['e','E','-','+'].includes(e.key) && e.preventDefault()}
                                onChange={e => updateDiscount(item.id, parseFloat(e.target.value) || 0)}
                                placeholder="0"
                                className="w-16 text-right text-sm border border-border rounded focus:outline-none focus:border-primary pl-4 pr-1.5 py-1 tabular-nums" />
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <div className="font-bold text-foreground text-sm tabular-nums">
                              ₹{item.total_price.toLocaleString('en-IN')}
                            </div>
                            {item.tax_amount > 0 && (
                              <div className="text-[10px] text-muted-foreground tabular-nums">+₹{item.tax_amount.toLocaleString('en-IN')} GST</div>
                            )}
                          </td>
                          <td className="px-2 py-2">
                            <button onClick={() => removeItem(item.id)}
                              className="w-5 h-5 flex items-center justify-center rounded text-muted-foreground/20 hover:text-destructive hover:bg-destructive/8 transition-all opacity-0 group-hover:opacity-100">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}

                    {/* ── Previous Balance row (display-only, no ledger impact) ── */}
                    {balanceForward > 0 && (
                      <tr className="border-b border-border/30 bg-destructive/3">
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1.5">
                            <div className="w-1.5 h-5 rounded-full flex-shrink-0 bg-destructive/40" />
                            <span className="text-xs text-muted-foreground/30 font-mono italic">—</span>
                          </div>
                        </td>
                        <td className="px-3 py-2" colSpan={hasGST ? 5 : 4}>
                          <div className="font-medium text-sm text-muted-foreground italic">Previous Balance</div>
                          <div className="text-[10px] text-muted-foreground/50">Outstanding from prior visits — settled on save</div>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="font-bold text-sm tabular-nums text-destructive">
                            ₹{balanceForward.toLocaleString('en-IN')}
                          </div>
                        </td>
                        <td className="px-2 py-2">
                          <button
                            onClick={() => setBalanceForward(0)}
                            className="w-5 h-5 flex items-center justify-center rounded text-muted-foreground/25 hover:text-destructive hover:bg-destructive/8 transition-all"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </td>
                      </tr>
                    )}

                    {/* ── Add service row — last row of the table ── */}
                    <tr className="border-b border-border/30 bg-muted/5">
                      <td className="px-3 py-2.5">
                        <span className="text-xs text-muted-foreground/25 font-mono">{billItems.length + 1}</span>
                      </td>
                      <td className="px-3 py-2" colSpan={hasGST ? 6 : 5}>
                        <div className="relative">
                          <div className="flex items-center gap-2">
                            <Search className="w-3.5 h-3.5 text-muted-foreground/30 flex-shrink-0" />
                            <input
                              ref={serviceSearchRef}
                              value={serviceSearch}
                              onChange={e => { setServiceSearch(e.target.value); setServiceDropdownOpen(true); }}
                              onFocus={() => setServiceDropdownOpen(true)}
                              onBlur={() => setTimeout(() => setServiceDropdownOpen(false), 150)}
                              placeholder="Add Service..."
                              className="flex-1 text-sm text-foreground placeholder:text-muted-foreground/35 bg-transparent border-0 focus:outline-none py-0"
                            />
                            {serviceSearch && (
                              <button onMouseDown={e => { e.preventDefault(); setServiceSearch(''); serviceSearchRef.current?.focus(); }}
                                className="text-muted-foreground/30 hover:text-muted-foreground flex-shrink-0">
                                <X className="w-3 h-3" />
                              </button>
                            )}
                          </div>

                          {/* Dropdown */}
                          {serviceDropdownOpen && (
                            <div ref={serviceDropdownRef}
                              className="absolute top-full left-0 z-30 mt-1 bg-popover border border-border rounded-xl shadow-xl overflow-y-auto"
                              style={{ width: 380, maxHeight: 260 }}>
                              {filteredServices.length === 0 ? (
                                <div className="px-4 py-5 text-center text-xs text-muted-foreground">
                                  {serviceSearch ? `No services match "${serviceSearch}"` : 'No active services'}
                                </div>
                              ) : filteredServices.map((svc, i) => {
                                const inBill = billItems.find(b => b.service_id === svc.id);
                                return (
                                  <button key={svc.id}
                                    onMouseDown={e => {
                                      e.preventDefault();
                                      addService(svc);
                                      setServiceSearch('');
                                      serviceSearchRef.current?.focus();
                                    }}
                                    className={cn(
                                      'w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left transition-colors',
                                      i > 0 && 'border-t border-border/40',
                                      inBill ? 'bg-primary/5 hover:bg-primary/10' : 'hover:bg-muted/50'
                                    )}>
                                    <span className={cn('text-sm truncate', inBill ? 'text-primary font-medium' : 'text-foreground')}>
                                      {svc.name}
                                    </span>
                                    <div className="flex items-center gap-2 shrink-0">
                                      {inBill && (
                                        <span className="text-[10px] font-bold text-primary bg-primary/10 px-1.5 py-0.5 rounded-full">
                                          ×{inBill.quantity}
                                        </span>
                                      )}
                                      <span className="text-sm font-semibold tabular-nums text-foreground">
                                        ₹{parseFloat(String(svc.price)).toLocaleString('en-IN')}
                                      </span>
                                    </div>
                                  </button>
                                );
                              })}
                              {/* Custom item — always shown at bottom when search has text */}
                              {serviceSearch.trim() && (
                                <button
                                  onMouseDown={e => {
                                    e.preventDefault();
                                    const customId = crypto.randomUUID?.() ?? Math.random().toString(36).slice(2);
                                    setBillItems(prev => [...prev, {
                                      id: customId, service_name: serviceSearch.trim(),
                                      category: 'other', item_type: 'other',
                                      quantity: 1, unit_price: 0, discount: 0,
                                      tax_rate: 0, tax_amount: 0, total_price: 0,
                                    }]);
                                    setServiceSearch('');
                                    serviceSearchRef.current?.focus();
                                  }}
                                  className={cn(
                                    'w-full flex items-center gap-2 px-4 py-2.5 text-left transition-colors hover:bg-muted/50',
                                    filteredServices.length > 0 && 'border-t border-border/40'
                                  )}
                                >
                                  <Plus className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                                  <span className="text-sm text-muted-foreground">
                                    Add <span className="font-medium text-foreground">"{serviceSearch.trim()}"</span> as custom item
                                  </span>
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      <td />
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* ══ RIGHT PANE: Checkout ══ */}
            {/* Structure: fixed top (summary) + scrollable middle (payment) + pinned bottom (actions) */}
            <div className="w-[300px] shrink-0 flex flex-col overflow-hidden bg-muted/25 border-l border-border">

              {/* ── Bills Summary — fixed top ── */}
              <div className="px-3 pt-3 pb-2 space-y-1.5 border-b border-border shrink-0">
                <div className="flex items-center justify-between">
                  <p className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">Bills Summary</p>
                  {availableAdvance > 0 && (
                    <span className="text-[10px] font-semibold tabular-nums" style={{ color: 'hsl(var(--success))' }}>
                      Deposit: ₹{availableAdvance.toLocaleString('en-IN')}
                    </span>
                  )}
                </div>

                {/* Pending bills covered by advance — no net debt but invoices are technically unpaid */}
                {pendingAmount > 0 && outstanding === 0 && availableAdvance > 0 && (
                  <div className="flex items-center gap-1.5 py-0.5 px-2 rounded-md bg-primary/5 border border-primary/15">
                    <FileText className="w-3 h-3 text-primary/60 shrink-0" />
                    <span className="text-[11px] text-primary/80">
                      ₹{pendingAmount.toLocaleString('en-IN')} in pending bills — advance covers them
                    </span>
                  </div>
                )}

                {outstanding > 0 && balanceForward === 0 && (
                  <div className="flex items-center justify-between py-0.5 px-2 rounded-md bg-destructive/6 border border-destructive/15">
                    <span className="text-[11px] text-destructive font-medium">
                      Past Due: ₹{outstanding.toLocaleString('en-IN')}
                    </span>
                    <button
                      onClick={() => setBalanceForward(outstanding)}
                      className="text-[10px] font-bold text-destructive hover:text-destructive/70 transition-colors ml-2 underline underline-offset-2"
                    >
                      Add to Bill
                    </button>
                  </div>
                )}
                {balanceForward > 0 && (
                  <div className="flex items-center justify-between py-0.5 px-2 rounded-md bg-destructive/6 border border-destructive/20">
                    <span className="text-[11px] text-destructive font-medium">
                      Previous Balance: ₹{balanceForward.toLocaleString('en-IN')} added
                    </span>
                    <button
                      onClick={() => setBalanceForward(0)}
                      className="text-[10px] font-bold text-muted-foreground hover:text-destructive transition-colors ml-2"
                    >
                      Remove
                    </button>
                  </div>
                )}

                {/* Deposit collection chip */}
                {!depositOpen && depositAmount === 0 ? (
                  <div className="flex items-center justify-between py-0.5 px-2 rounded-md bg-muted/40 border border-border/50">
                    <span className="text-[11px] text-muted-foreground">Collect Deposit?</span>
                    <button
                      onClick={() => setDepositOpen(true)}
                      className="text-[10px] font-bold text-primary hover:text-primary/70 transition-colors ml-2 underline underline-offset-2"
                    >
                      + Add
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 py-0.5 px-2 rounded-md bg-primary/6 border border-primary/20">
                    <span className="text-[11px] font-medium flex-1" style={{ color: 'hsl(var(--primary))' }}>
                      Deposit:
                    </span>
                    <div className="relative flex items-center">
                      <span className="absolute left-1.5 text-[11px] text-muted-foreground pointer-events-none">₹</span>
                      <input
                        type="number" min={0}
                        value={depositAmount === 0 ? '' : depositAmount}
                        onKeyDown={e => ['e','E','-','+'].includes(e.key) && e.preventDefault()}
                        onChange={e => setDepositAmount(Math.max(0, parseFloat(e.target.value) || 0))}
                        placeholder="0"
                        autoFocus
                        className="w-16 text-right text-[11px] font-bold pl-4 pr-1.5 py-0.5 border border-border rounded focus:outline-none focus:border-primary bg-background tabular-nums"
                      />
                    </div>
                    <button
                      onClick={() => { setDepositAmount(0); setDepositOpen(false); }}
                      className="text-muted-foreground/30 hover:text-destructive transition-colors ml-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}

                <div className="space-y-1">
                  <SummaryRow label="Total Billed" value={`₹${subtotal.toLocaleString('en-IN')}`} />

                  {canDiscount && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">Discount</span>
                      <div className="flex items-center gap-1">
                        <div className="flex rounded border border-border overflow-hidden">
                          {(['flat', 'percent'] as const).map(t => (
                            <button
                              key={t}
                              onClick={() => { setDiscountType(t); setDiscount(d => t === 'percent' ? Math.min(d, 100) : Math.min(d, subtotal)); }}
                              className={cn('px-1.5 py-0.5 text-[10px] font-bold transition-colors',
                                discountType === t ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted')}
                            >
                              {t === 'flat' ? '₹' : '%'}
                            </button>
                          ))}
                        </div>
                        <input
                          type="number" min={0} max={discountType === 'percent' ? 100 : subtotal}
                          value={discount === 0 ? '' : discount}
                          onKeyDown={e => ['e','E','-','+'].includes(e.key) && e.preventDefault()}
                          onChange={e => {
                            const v = parseFloat(e.target.value) || 0;
                            setDiscount(discountType === 'percent' ? Math.min(100, v) : Math.min(subtotal, v));
                          }}
                          placeholder="0"
                          className="w-14 text-right text-xs font-semibold border border-border rounded px-1.5 py-0.5 focus:outline-none focus:border-primary bg-background tabular-nums"
                        />
                      </div>
                    </div>
                  )}
                  {discountAmount > 0 && <SummaryRow label="Discount" value={`−₹${discountAmount.toLocaleString('en-IN')}`} valueClass="text-success" />}
                  {taxTotal > 0 && <SummaryRow label="Tax" value={`₹${taxTotal.toLocaleString('en-IN')}`} />}
                  <SummaryRow label="Services Total" value={`₹${finalTotal.toLocaleString('en-IN')}`} bold />
                  {balanceForward > 0 && (
                    <SummaryRow label="Previous Balance" value={`₹${balanceForward.toLocaleString('en-IN')}`} valueClass="text-destructive" />
                  )}
                  {depositAmount > 0 && (
                    <SummaryRow label="Deposit Collected" value={`₹${depositAmount.toLocaleString('en-IN')}`} valueClass="text-primary" />
                  )}
                  <SummaryRow label="Received" value={`₹${paidAmount.toLocaleString('en-IN')}`} />
                  <SummaryRow
                    label="Balance"
                    value={`₹${balanceAmount.toLocaleString('en-IN')}`}
                    valueClass={trueOverpaid > 0 ? 'text-destructive' : balanceAmount > 0 ? 'text-warning' : 'text-success'}
                  />
                </div>

                {/* Total anchor */}
                <div className="pt-1 border-t border-border flex items-baseline justify-between">
                  <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
                    {(balanceForward > 0 || depositAmount > 0) ? 'TO COLLECT' : 'TOTAL'}
                  </span>
                  <span className="text-xl font-black tabular-nums tracking-tight" style={{ color: 'hsl(var(--primary))' }}>
                    ₹{totalToCollect.toLocaleString('en-IN')}
                  </span>
                </div>
              </div>

              {/* ── Payment — scrollable middle ── */}
              <div className="px-3 py-2.5 space-y-2 flex-1 overflow-y-auto">
                <p className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">Payment</p>

                <div className="space-y-2">
                  {payments.map(row => (
                    <div key={row.id} className="space-y-1.5">
                      <div className="flex items-center gap-1.5">
                        <select
                          value={row.mode}
                          onChange={e => updatePaymentRow(row.id, { mode: e.target.value, referenceNumber: '', upiApp: '' })}
                          className="text-xs border border-border rounded-lg px-2 py-1 bg-background focus:outline-none focus:border-primary flex-shrink-0 w-[72px] font-medium"
                        >
                          {paymentMethods.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>

                        <div className="relative flex-1">
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-xs pointer-events-none">₹</span>
                          <input
                            type="number" min={0}
                            max={row.mode === 'Advance' ? availableAdvance : undefined}
                            value={row.amount === 0 ? '' : row.amount}
                            onKeyDown={e => ['e','E','-','+'].includes(e.key) && e.preventDefault()}
                            onChange={e => {
                              const v = parseFloat(e.target.value);
                              const raw = isNaN(v) ? 0 : Math.max(0, v);
                              updatePaymentRow(row.id, { amount: row.mode === 'Advance' ? Math.min(raw, availableAdvance) : raw });
                            }}
                            placeholder="0"
                            className="w-full pl-6 pr-2 py-1 text-sm font-bold rounded-lg focus:outline-none transition-all tabular-nums border-2 bg-background"
                            style={{ borderColor: row.amount > 0 ? 'hsl(var(--primary))' : 'hsl(var(--border))' }}
                          />
                        </div>

                        <button
                          onClick={() => setRowToFull(row.id)}
                          className="text-[11px] font-bold px-2 py-1 rounded-lg border border-border text-muted-foreground hover:border-primary/40 hover:text-primary transition-colors shrink-0"
                        >
                          Full
                        </button>

                        {payments.length > 1 && (
                          <button
                            onClick={() => removePaymentRow(row.id)}
                            className="text-muted-foreground/35 hover:text-destructive transition-colors shrink-0"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>

                      {row.mode === 'UPI' && (
                        <div className="flex gap-1.5">
                          <select
                            value={row.upiApp}
                            onChange={e => updatePaymentRow(row.id, { upiApp: e.target.value })}
                            className="text-xs border border-border rounded-lg px-2 py-1 bg-background focus:outline-none w-24"
                          >
                            <option value="">App</option>
                            {['gpay','phonepe','paytm','bhim','other'].map(a => (
                              <option key={a} value={a}>{a.charAt(0).toUpperCase() + a.slice(1)}</option>
                            ))}
                          </select>
                          <input
                            value={row.referenceNumber}
                            onChange={e => updatePaymentRow(row.id, { referenceNumber: e.target.value })}
                            placeholder="Txn ID"
                            className="flex-1 text-xs border border-border rounded-lg px-2 py-1 font-mono focus:outline-none focus:border-primary"
                          />
                        </div>
                      )}
                      {row.mode === 'Cash' && (
                        <input
                          value={row.referenceNumber}
                          onChange={e => updatePaymentRow(row.id, { referenceNumber: e.target.value })}
                          placeholder="Receipt no. (optional)"
                          className="w-full text-xs border border-border rounded-lg px-2 py-1 font-mono focus:outline-none focus:border-primary"
                        />
                      )}
                      {row.mode === 'Card' && (
                        <input
                          value={row.referenceNumber}
                          onChange={e => updatePaymentRow(row.id, { referenceNumber: e.target.value })}
                          placeholder="Card transaction ID (optional)"
                          className="w-full text-xs border border-border rounded-lg px-2 py-1 font-mono focus:outline-none focus:border-primary"
                        />
                      )}
                      {row.mode === 'Cheque' && (
                        <input
                          value={row.referenceNumber}
                          onChange={e => updatePaymentRow(row.id, { referenceNumber: e.target.value })}
                          placeholder="Cheque no."
                          className="w-full text-xs border border-border rounded-lg px-2 py-1 font-mono focus:outline-none focus:border-primary"
                        />
                      )}
                      {row.mode === 'Advance' && (
                        <p className="text-[10px] text-muted-foreground">
                          ₹{availableAdvance.toLocaleString('en-IN')} available
                        </p>
                      )}
                    </div>
                  ))}

                  {payments.length < paymentMethods.length && (
                    <button
                      onClick={addPaymentRow}
                      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors py-0.5 font-medium"
                    >
                      <Plus className="w-3 h-3" /> Add payment mode
                    </button>
                  )}
                </div>

                {/* Status row */}
                <div className="flex items-center justify-between pt-0.5">
                  <span className="text-xs text-muted-foreground tabular-nums">₹{billPaidAmount.toLocaleString('en-IN')} received</span>
                  <span
                    className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold"
                    style={{ background: statusConfig.bg, color: statusConfig.color }}
                  >
                    <statusConfig.icon className="w-3 h-3" />
                    {paymentStatus}
                  </span>
                </div>
                {settlementAmount > 0 && (
                  <p className="text-xs flex items-center gap-1 font-medium" style={{ color: 'hsl(var(--success))' }}>
                    <CheckCircle className="w-3 h-3" />
                    ₹{settlementAmount.toLocaleString('en-IN')} → clears outstanding dues
                  </p>
                )}
                {trueOverpaid > 0 && (
                  <p className="text-xs text-destructive flex items-center gap-1 font-medium">
                    <AlertCircle className="w-3 h-3" /> Over by ₹{trueOverpaid.toLocaleString('en-IN')}
                  </p>
                )}
                {trueOverpaid === 0 && settlementAmount === 0 && paidAmount > 0 && balanceAmount > 0 && (
                  <p className="text-xs text-warning flex items-center gap-1 font-medium">
                    <AlertCircle className="w-3 h-3" /> Balance ₹{balanceAmount.toLocaleString('en-IN')} due
                  </p>
                )}
              </div>

              {/* ── Notes + Actions — pinned bottom ── */}
              <div className="px-3 pt-2 pb-3 space-y-2 shrink-0 border-t border-border">
                <input
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="Notes (optional)"
                  className="w-full text-xs border border-border rounded-lg px-3 py-1.5 focus:outline-none focus:border-primary bg-background text-foreground placeholder:text-muted-foreground/50"
                />

                <div className="flex gap-2">
                  <button
                    onClick={onClose}
                    disabled={loading}
                    className="flex-1 py-1.5 text-sm font-semibold text-muted-foreground border border-border rounded-xl hover:bg-muted transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>

                  {/* Create Bill — the money button */}
                  <button
                    onClick={handleCreate}
                    disabled={loading || billItems.length === 0 || !hasPatient || trueOverpaid > 0}
                    className="flex-[2] py-2 text-sm font-bold text-primary-foreground rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
                    style={{
                      background: loading || billItems.length === 0 || !hasPatient || trueOverpaid > 0
                        ? 'hsl(var(--primary) / 0.4)'
                        : paymentStatus === 'Paid'
                          ? 'hsl(var(--success))'
                          : 'hsl(var(--primary))',
                      boxShadow: (!loading && billItems.length > 0 && hasPatient && trueOverpaid === 0)
                        ? '0 2px 12px hsl(var(--primary) / 0.35)'
                        : 'none',
                    }}
                  >
                    {loading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Creating…</>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        {paymentStatus === 'Paid'
                          ? `Paid · ₹${finalTotal.toLocaleString('en-IN')}`
                          : paymentStatus === 'Partial'
                            ? `₹${paidAmount.toLocaleString('en-IN')} / ₹${finalTotal.toLocaleString('en-IN')}`
                            : `Create Bill`
                        }
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
